import { cn } from "../../lib/cn";

/**
 * Reusable "agent working" indicator — a small comet of accent dots orbiting
 * a faint center, over a soft breathing glow. Calm and technical rather than a
 * busy spinner; reads as "thinking" at any size and looks right on the dark
 * canvas. The orbit/breath keyframes live in index.css and self-disable under
 * `prefers-reduced-motion`, where the dots simply hold still but stay legible.
 *
 * Drop-in anywhere a running state needs a live cue: the Activity header, tool
 * cards mid-call, chat. Decorative by default — pass a `label` to announce it.
 */
interface Props {
  /** Glyph size in px (square). */
  size?: number;
  /** Accessible label; when omitted the glyph is purely decorative. */
  label?: string;
  className?: string;
}

// A short arc of dots (a "comet"), brightest at the head, that rotates. Angles
// measured from the top, clockwise; opacity trails off behind the head.
const DOTS = [
  { angle: 0, opacity: 1 },
  { angle: 48, opacity: 0.55 },
  { angle: 96, opacity: 0.28 },
];
const R = 5; // orbit radius within the 16-unit viewBox
const DOT_R = 1.25;

export function BufferIndicator({ size = 16, label, className }: Props) {
  return (
    <span
      role={label ? "status" : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
      className={cn(
        "relative inline-flex items-center justify-center shrink-0",
        className,
      )}
      style={{ width: size, height: size }}
    >
      {/* Soft breathing glow */}
      <span
        aria-hidden="true"
        className="absolute inset-0 rounded-full activity-breathe"
        style={{
          background:
            "radial-gradient(circle, rgb(var(--accent-rgb) / 0.35) 0%, transparent 70%)",
        }}
      />
      <svg
        viewBox="0 0 16 16"
        width={size}
        height={size}
        fill="none"
        aria-hidden="true"
        className="relative activity-orbit"
      >
        {/* faint center anchor */}
        <circle cx="8" cy="8" r="1.1" fill="var(--accent)" opacity="0.3" />
        {DOTS.map((d) => {
          const rad = (d.angle * Math.PI) / 180;
          const cx = 8 + R * Math.sin(rad);
          const cy = 8 - R * Math.cos(rad);
          return (
            <circle
              key={d.angle}
              cx={cx}
              cy={cy}
              r={DOT_R}
              fill="var(--accent)"
              opacity={d.opacity}
            />
          );
        })}
      </svg>
    </span>
  );
}
